// i18n
import { useTranslation } from 'react-i18next';

// types
import { Entity, TraceabilityType } from '../../../@types';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Grid, Paper, Typography } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { useDrawingArea } from '@mui/x-charts';
import { styled } from '@mui/material/styles';

// theme
import { theme } from '../../../theme';
import { useState } from 'react';
import { traceabilityDefault } from '../../../utils/traceability';

const StyledText = styled('text')(({ theme }) => ({
	textAnchor: 'middle',
	dominantBaseline: 'central',
	fontSize: 18,
	fontWeight: 600,
}));

// props
interface CardTraceabilityProps {
	entityId: number;
	type?: TraceabilityType;
}

export const CardTraceability = ({ entityId, type }: CardTraceabilityProps) => {
	// session
	const token = sessionStorage.getItem('token');

	// hooks
	const { t } = useTranslation();

	// state
	const [entity, setEntity] = useState<Entity | null>(null);

	// vars
	let traceability = { ...traceabilityDefault };

	const isTransactions = type === 'TRANSACTIONS' && traceability && 'documentation' in traceability;
	const isFinancial = type === 'FINANCIAL' && traceability && 'financial' in traceability;
	const pieParams = { innerRadius: 50, height: 100, margin: { right: 5 } };

	// fetch entities
	useFetch({
		isEnabled: Boolean(entityId),
		url: `${process.env.REACT_APP_API_URL}/api/proxies/entities/${entityId}`,
		options: {
			headers: {
				Authorization: `Bearer ${token}`,
				apikey: process.env.REACT_APP_APIKEY || '',
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setEntity(res.data[0]);
			}
		},
	});

	// define default colors
	const colors = {
		background: theme.palette.brand.grey[100],
		border: theme.palette.brand.grey[300],
		color: theme.palette.brand.grey[700],
	};

	let value: number = 0;

	if (entity) {
		traceability = entity.traceability;

		if (isTransactions) {
			value = traceability.documentation.value || 0;
		} else if (isFinancial) {
			value = traceability.financial.value || 0;
		}

		if (traceability?.documentation && typeof traceability?.documentation.value === 'number') {
			value = traceability.documentation.value;

			if (value >= 1 && value <= 49) {
				colors.background = theme.palette.brand.orange[100];
				colors.border = theme.palette.brand.orange[300];
				colors.color = theme.palette.brand.orange[700];
			} else if (value >= 50 && value <= 70) {
				colors.background = theme.palette.brand.yellow[100];
				colors.border = theme.palette.brand.yellow[300];
				colors.color = theme.palette.brand.yellow[700];
			} else if (value >= 71) {
				colors.background = theme.palette.brand.green[100];
				colors.border = theme.palette.brand.green[300];
				colors.color = theme.palette.brand.green[700];
			}
		}
	}

	const PieCenterLabel = ({ children }: { children: React.ReactNode }) => {
		const { width, height, left, top } = useDrawingArea();
		return (
			<StyledText fill={colors.color} x={left + width / 2} y={top + height / 2}>
				{children}
			</StyledText>
		);
	};

	// vars
	return (
		<Paper
			elevation={0}
			sx={(theme) => {
				return {
					color: colors.color,
					backgroundColor: colors.background,
					border: `2px transparent solid`,
					borderColor: colors.border,
					height: '100%',
					padding: theme.spacing(3, 3, 3, 0),
				};
			}}>
			<Grid alignItems="center" container>
				<Grid item xs={5}>
					<PieChart
						series={[
							{
								data: [
									{ value: 100 - value, color: colors.border },
									{ value: value, color: colors.color },
								],
								innerRadius: 40,
								outerRadius: 50,
							},
						]}
						{...pieParams}>
						<PieCenterLabel>{`${Math.round(value)}%`}</PieCenterLabel>
					</PieChart>
				</Grid>
				<Grid item xs={true}>
					<Typography variant="h2">{entity?.name}</Typography>
					<Typography variant="h6">{t('traceability')}</Typography>
					{traceability?.documentation && (
						<Typography>
							{t('transactionsDocumented', {
								documented: traceability.documentation.documented,
								total: traceability.documentation.total,
							})}
						</Typography>
					)}
				</Grid>
			</Grid>
		</Paper>
	);
};
